/**
 * Best-effort message out of whatever DRF (or RTK Query) handed back.
 *
 * The API answers with several shapes — `{message}`, `{error}`,
 * `{non_field_errors: [...]}`, `{field: [...]}` — and some of them arrive as
 * the string form of a Python list, so the wrapping brackets and quotes are
 * unwrapped rather than shown to the operator.
 */
export const extractApiError = (err: any): string => {
	try {
		const data = (err && (err.data ?? err.error)) ?? err;
		if (!data) return 'An unexpected error occurred';

		if (typeof data === 'string') {
			const m = data.match(/^\s*\[\s*['"](.+?)['"]\s*\]\s*$/);
			return m && m[1] ? m[1] : data;
		}

		if ((data as any).message) {
			const msg = (data as any).message;
			if (Array.isArray(msg)) return msg.join(' ');
			if (typeof msg === 'string') {
				const m = msg.match(/^\s*\[\s*['"](.+?)['"]\s*\]\s*$/);
				return m && m[1] ? m[1] : msg;
			}
			return String(msg);
		}

		if ((data as any).error) {
			const errMsg = (data as any).error;
			if (Array.isArray(errMsg)) return errMsg.join(' ');
			return String(errMsg);
		}

		if ((data as any).non_field_errors) {
			const nfe = (data as any).non_field_errors;
			if (Array.isArray(nfe)) return nfe.join(' ');
			return String(nfe);
		}

		const parts: string[] = [];
		Object.entries(data as any).forEach(([k, v]) => {
			if (Array.isArray(v)) parts.push(`${k}: ${v.join(', ')}`);
			else if (typeof v === 'string') parts.push(`${k}: ${v}`);
		});
		return parts.length ? parts.join(' | ') : 'An unexpected error occurred';
	} catch (_) {
		return 'An unexpected error occurred';
	}
};
